// Le fil d'accueil — le dernier grand écran, et celui que tout le monde ouvre
// en premier.
//
// Le bandeau des statuts reste accueilli tel quel ; la carte de publication,
// elle, est passée à React à son tour (`react/Publication.jsx`). Ce qui est
// écrit ici, c'est le verset du jour, le composeur, les filtres, et la mécanique
// de classement.
//
// Deux choses valent d'être dites avant de lire le code.
//
// 1. **L'ordre du fil est FIGÉ pour ce qui est déjà à l'écran.** Le classement
//    ne s'applique qu'à ce qui arrive. Sans cela, chaque page chargée au
//    défilement réorganiserait ce que le membre est en train de lire — c'est le
//    rôle de `dejaAffiches`.
// 2. **Sans collecteur de signaux, on garde l'ordre chronologique.** Pendant la
//    première seconde après la connexion, mieux vaut un fil brut qu'un fil vide.

import { useCallback, useRef } from "react";
import Store from "../../store.js";
import { versetDuJour, jourLong } from "../../ui.js";
import { classerFil } from "../../algorithmes.js";
import { estSuspendu } from "../../views/moderation.js";
import BandeauStories from "../BandeauStories.jsx";
import { CarteEvenement } from "./Evenements.jsx";
import { estPasse, enDirect } from "../dates.js";
import ListePublications from "../ListePublications.jsx";
import { useAbonnement, useSignaux } from "../hooks.js";
import Composeur, { ComposeurBloque } from "../Composeur.jsx";
import SuggestionsMembres from "../SuggestionsMembres.jsx";
import Anniversaires from "../Anniversaires.jsx";

// Les vidéos ont leur propre onglet (Reels) : pas de puce « Vidéos » ici.
const FILTRES = [
  { id: "tous", label: "Tout" },
  { id: "priere", label: "🙏 Prières" },
  { id: "temoignage", label: "✨ Témoignages" },
  { id: "verset", label: "📖 Versets" },
];

const garder = (p, filtre) =>
  filtre === "tous" ? true : filtre === "video" ? Boolean(p.video) : p.type === filtre;

/* ------------------------------------------------------- prochains rendez-vous

   Deux au maximum, et seulement s'il y en a : un rappel qui s'affiche vide ne
   rappelle rien. C'est le seul endroit où l'on peut annoncer un culte à
   quelqu'un qui n'est pas allé le chercher — donc il vaut sa place en haut. */

function Prochains({ ctx }) {
  const tous = useAbonnement((cb) => Store.evenements.subscribe(cb), [], []);
  const direct = tous.find(enDirect);
  const aVenir = tous.filter((e) => !estPasse(e.quand) && e.id !== direct?.id).slice(0, 2);

  return (
    <>
      {/* Un direct en cours passe AVANT tout le reste : il ne se rattrape pas. */}
      {direct && (
        <a className="direct-bandeau" href={`#/evenement/${direct.id}`}>
          <span className="direct-pastille" />
          <span>
            <strong>EN DIRECT</strong>
            <span className="petit">{direct.titre}</span>
          </span>
          <span className="btn btn--fantome btn--petit">Regarder</span>
        </a>
      )}
      {aVenir.length > 0 && <Rappels aVenir={aVenir} />}
    </>
  );
}

function Rappels({ aVenir }) {
  return (
    <section className="prochains">
      <div className="prochains__titre">
        <span>🗓️ Prochains rendez-vous</span>
        <a className="btn btn--fantome btn--petit" href="#/evenements">
          Tout voir
        </a>
      </div>
      {aVenir.map((e) => (
        <CarteEvenement e={e} key={e.id} />
      ))}
    </section>
  );
}

/* ---------------------------------------------------------- verset du jour */

function CarteVerset({ ctx, onPartager }) {
  const v = versetDuJour();
  return (
    <section className="verset">
      <div className="verset__label">📖 Verset du jour · {jourLong(Date.now())}</div>
      <blockquote>« {v.t} »</blockquote>
      <cite>{v.r}</cite>
      {!estSuspendu(ctx.moi) && (
        <button className="btn btn--fantome btn--petit" onClick={() => onPartager(v)}>
          Partager ce verset
        </button>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------- écran */

export default function Fil({ ctx, params }) {
  const filtre = params.filtre || "tous";
  const versionSignaux = useSignaux(ctx.signaux);
  const composeur = useRef(null);
  // Les amitiés, pour la carte d'accueil ci-dessous : c'est elle qui décide de
  // s'afficher ou non, et elle ne peut le faire sans savoir si l'on est seul.
  const relations = useAbonnement((cb) => Store.amis.subscribe(cb), []);

  // L'ordre déjà montré au membre. Une RÉFÉRENCE et non un état : le modifier
  // ne doit rien redessiner — il ne sert qu'au prochain classement.
  const dejaAffiches = useRef([]);

  const classer = useCallback(
    (tout) => {
      const visibles = tout.filter((p) => garder(p, filtre));
      if (!ctx.signaux) return { posts: visibles };

      ctx.signaux.apprendrePosts(visibles);
      const classe = classerFil(visibles, ctx.signaux.instantane(), {
        dejaAffiches: dejaAffiches.current,
      });
      dejaAffiches.current = classe.map((r) => r.post.id);
      return {
        posts: classe.map((r) => r.post),
        raisons: new Map(classe.map((r) => [r.post.id, r.raisons])),
      };
    },
    [filtre, ctx]
  );

  const peutEcrire = ctx.moi?.emailVerifie !== false && !estSuspendu(ctx.moi);

  return (
    <div className="colonne">
      <BandeauStories ctx={ctx} />

      {/* Après les statuts, avant le verset : ce qui arrive passe devant ce
          qui se lit. */}
      <Prochains ctx={ctx} />

      {filtre === "tous" && (
        <CarteVerset ctx={ctx} onPartager={(v) => composeur.current?.remplir(v)} />
      )}

      {peutEcrire ? <Composeur ctx={ctx} ref={composeur} /> : <ComposeurBloque moi={ctx.moi} />}

      {/* ⚠️ LES ANNIVERSAIRES PASSENT DEVANT LES SUGGESTIONS, et c'est l'ordre
          juste : un anniversaire est daté — il ne vaut que CE jour-là, et
          manqué il est manqué. Une suggestion d'ami sera encore là demain. La
          carte disparaît d'elle-même les jours où personne n'est né. */}
      <Anniversaires ctx={ctx} relations={relations} />

      {/* Les premiers jours seulement : la carte se retire d'elle-même dès que
          le membre a un ami. Elle est ici, sous le composeur et au-dessus du
          fil — exactement là où se trouve le vide qu'elle comble. */}
      <SuggestionsMembres ctx={ctx} relations={relations} />

      <div className="filtres" role="tablist">
        {FILTRES.map((f) => (
          <a
            key={f.id}
            className={`chip${f.id === filtre ? " est-actif" : ""}`}
            href={`#/fil${f.id === "tous" ? "" : "/" + f.id}`}
          >
            {f.label}
          </a>
        ))}
      </div>

      <ListePublications
        ctx={ctx}
        chargerPage={(o) => Store.posts.page(o)}
        abonnerRecentes={(cb) => Store.posts.subscribe(cb)}
        transformer={classer}
        rafraichirQuand={versionSignaux}
        deps={[filtre]}
        /* Un fil vide n'est plus une anomalie : c'est l'état NORMAL de qui
           vient d'arriver. Koinonia ne montre rien de la communauté tant qu'on
           n'a ajouté personne. L'écran doit donc dire quoi faire, sinon
           l'arrivant croit à une panne et s'en va. */
        vide={[
          "🕊️",
          "Votre fil est encore vide",
          "Vous ne voyez ici que vos amis et les membres que vous suivez. Ouvrez l'annuaire, ajoutez quelqu'un ou abonnez-vous — le fil se remplira de lui-même.",
        ]}
      />
    </div>
  );
}
